/* eslint-disable max-classes-per-file */
import React, { PureComponent } from 'react'
import Login from 'pages/login'
import { initializeIcons } from '@uifabric/icons'
import { connect } from 'react-redux'
import Layout from 'pages/_layout'
import { loadTheme, MessageBarType } from '@fluentui/react'
import { PrivateRoute, PublicRoute } from 'components/routes'
import history from 'helpers/history'
import Modal from 'components/containers/modal'
// eslint-disable-next-line import/named
import withHandlers, { HandlersProps } from 'helpers/hoc/withHandlers'
import PublicLayout from 'pages/_publicLayout'
import {
    RouteChildrenProps, Switch, Router, Redirect,
} from 'react-router-dom'
import IndexUsers from 'pages/users'
import IdUsers from 'pages/users/[id]'
import IdExports from 'pages/exports/[id]'
// eslint-disable-next-line import/named
import { mapStateToProps, mapDispatchToProps, ReduxProps } from 'redux/map'
import NotFound from 'pages/notFound'
import User from 'requests/objects/user'
import Param from 'requests/objects/param'
import { NEW_PATH } from 'types/others'
import Export from 'pages/export'
import LastExports from 'pages/lastExports'

/**
 * Global components props
 * @typedef {ReduxProps & HandlersProps & RouteChildrenProps} AppProps
 */

initializeIcons('https://static2.sharepointonline.com/files/fabric/assets/icons/')
// https://fabricweb.z5.web.core.windows.net/pr-deploy-site/refs/heads/master/theming-designer/index.html
loadTheme({
    palette: {
        themePrimary: '#2b6ca3',
        themeLighterAlt: '#f4f8fb',
        themeLighter: '#d4e3f0',
        themeLight: '#b1cce3',
        themeTertiary: '#6f9ec8',
        themeSecondary: '#3d79ae',
        themeDarkAlt: '#266093',
        themeDark: '#20517c',
        themeDarker: '#183c5b',
        neutralLighterAlt: '#f8f8f8',
        neutralLighter: '#f4f4f4',
        neutralLight: '#eaeaea',
        neutralQuaternaryAlt: '#dadada',
        neutralQuaternary: '#d0d0d0',
        neutralTertiaryAlt: '#c8c8c8',
        neutralTertiary: '#bab8b7',
        neutralSecondary: '#a3a2a0',
        neutralPrimaryAlt: '#8d8b8a',
        neutralPrimary: '#323130',
        neutralDark: '#605e5d',
        black: '#494847',
        white: '#ffffff',
    },
})

const AppLayout = connect(mapStateToProps, mapDispatchToProps)(withHandlers(Layout))
const AppPublicLayout = connect(mapStateToProps, mapDispatchToProps)(withHandlers(PublicLayout))

const AppModal = connect(mapStateToProps, mapDispatchToProps)(Modal)

const AppLogin = connect(mapStateToProps, mapDispatchToProps)(withHandlers(Login))
const AppExport = connect(mapStateToProps, mapDispatchToProps)(withHandlers(Export))
const AppLastExports = connect(mapStateToProps, mapDispatchToProps)(withHandlers(LastExports))
const AppIndexUser = connect(mapStateToProps, mapDispatchToProps)(withHandlers(IndexUsers))
const AppIdUser = connect(mapStateToProps, mapDispatchToProps)(withHandlers(IdUsers))
const AppIdExport = connect(mapStateToProps, mapDispatchToProps)(withHandlers(IdExports))
const AppError = connect(mapStateToProps, mapDispatchToProps)(NotFound)

/**
 * @augments {React.PureComponent<AppProps>}
 */
class _App extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            isInit: false,
        }
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        const { isAuthenticated } = this.props

        history.replace({ ...history.location, state: null }) // Force to clean history state on each refresh

        if (isAuthenticated)
            this.init()
    }

    /**
     * @inheritdoc
     * @param {object} prevProps Previous props
     */
    componentDidUpdate(prevProps) {
        const { isAuthenticated } = this.props

        if (isAuthenticated && isAuthenticated !== prevProps.isAuthenticated)
            this.init()
    }

    /**
     * Init app by getting constants and user infos
     */
    init() {
        const {
            setMessageBar, setCommand, setBreadcrumb, paramsHandler, usersHandler, init, signOut,
        } = this.props

        setMessageBar({ isDisplayed: false })
        setCommand([])
        setBreadcrumb([])
        this.setState({ isInit: false }, async () => {
            try {
                const lastUpdate = await paramsHandler.getLastUpdate().fetch()
                const lastUpdateLocal = (() => {
                    const lastUpdateStorage = new Date(localStorage.getItem(`${process.env.REACT_APP_BASE_STORAGE_KEY}_last_update`))
                    return lastUpdateStorage instanceof Date && !Number.isNaN(lastUpdateStorage.getTime()) ? lastUpdateStorage : null
                })()

                if (
                    !lastUpdateLocal
                    || lastUpdate > lastUpdateLocal
                    || !localStorage.getItem(`${process.env.REACT_APP_BASE_STORAGE_KEY}_me`)
                    || !localStorage.getItem(`${process.env.REACT_APP_BASE_STORAGE_KEY}_param`)
                ) {
                    const [me, param] = await Promise.all([
                        usersHandler.getMe().fetch(),
                        paramsHandler.getAll().fetch(),
                    ])
                    init({ me, param, lastUpdate })
                } else {
                    init({
                        me: new User(JSON.parse(localStorage.getItem(`${process.env.REACT_APP_BASE_STORAGE_KEY}_me`))),
                        param: new Param(JSON.parse(localStorage.getItem(`${process.env.REACT_APP_BASE_STORAGE_KEY}_param`))),
                        lastUpdate,
                    })
                    this.setState({ isInit: true })
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error)
                setMessageBar({
                    isDisplayed: true,
                    type: MessageBarType.error,
                    message: error,
                })
                signOut()
            } finally {
                this.setState({ isInit: true })
            }
        })
    }

    render() {
        const { isAuthenticated } = this.props
        const { isInit } = this.state

        return (
            <>
                <Router history={history}>
                    <AppLayout
                        isDisplay={isAuthenticated}
                        refresh={() => {
                            localStorage.removeItem(`${process.env.REACT_APP_BASE_STORAGE_KEY}_me`)
                            localStorage.removeItem(`${process.env.REACT_APP_BASE_STORAGE_KEY}_param`)
                            localStorage.removeItem(`${process.env.REACT_APP_BASE_STORAGE_KEY}_last_update`)
                            this.init()
                        }}
                    >
                        <Switch>
                            <PrivateRoute
                                path={`/users/${NEW_PATH}`}
                                component={AppIdUser}
                                isAuthenticated={isAuthenticated}
                                isInit={isInit}
                                title="Nouvel utilisateur"
                            />
                            <PrivateRoute
                                path="/users/:userId"
                                component={AppIdUser}
                                isAuthenticated={isAuthenticated}
                                isInit={isInit}
                                title={props => `Utilisateur n°${props.userId}`}
                            />
                            <PrivateRoute
                                path="/users"
                                component={AppIndexUser}
                                isAuthenticated={isAuthenticated}
                                isInit={isInit}
                                title="users"
                            />
                            <PrivateRoute
                                path="/export"
                                component={AppExport}
                                isAuthenticated={isAuthenticated}
                                isInit={isInit}
                                title="Nouvel export"
                            />
                            <PrivateRoute
                                path="/last-exports"
                                component={AppLastExports}
                                isAuthenticated={isAuthenticated}
                                isInit={isInit}
                                title="Derniers exports"
                            />
                            <PrivateRoute
                                path="/exports/:exportId"
                                component={AppIdExport}
                                isAuthenticated={isAuthenticated}
                                isInit={isInit}
                                title="Export"
                            />
                            <PrivateRoute
                                exact
                                path="/"
                                component={AppExport}
                                isAuthenticated={isAuthenticated}
                                isInit={isInit}
                                title="Accueil"
                            />
                            {isAuthenticated && (
                                <PublicRoute
                                    component={AppError}
                                    title="Error"
                                />
                            )}
                        </Switch>
                    </AppLayout>

                    <AppPublicLayout
                        isDisplay={!isAuthenticated}
                    >
                        <Switch>
                            <PublicRoute
                                path="/login"
                                component={AppLogin}
                                title="Connexion"
                            />
                            {!isAuthenticated && (
                                <Redirect to="/login" />
                            )}
                        </Switch>
                    </AppPublicLayout>
                </Router>
                <AppModal />
            </>
        )
    }
}

const App = connect(mapStateToProps, mapDispatchToProps)(withHandlers(_App))

export default App
